import { FC, ReactNode } from 'react';
import { cn } from 'src/utils/utility';
import { Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

export interface MetricOption<T> {
  id: T;
  label: ReactNode;
  labelText?: string;
  icon: string;
}

export interface MetricsSelectorUIProps<T> {
  selected: T[];
  metrics: MetricOption<T>[];
  isMaxSelected: boolean;
  onToggle: (id: T) => void;
  onReset: () => void;
  onApply: () => void;
  iconClassName?: string;
}

/**
 * Shared UI component for MetricsSelector
 * Reduces duplication between Dashboard and Analytics MetricsSelector components
 */
export const MetricsSelectorUI = <T extends string>({
  selected,
  metrics,
  isMaxSelected,
  onToggle,
  onReset,
  onApply,
  iconClassName = 'w-16 h-16',
}: MetricsSelectorUIProps<T>) => {
  return (
    <div className='w-[300px] bg-white rounded-lg shadow-xl border border-neutral-20 flex flex-col overflow-hidden'>
      {/* Header */}
      <div className='px-16 pt-12 pb-8 border-b border-neutral-20 flex justify-between items-center'>
        <div className='flex flex-col'>
          <span className='font-bold text-neutral-100 text-base'>Metrics</span>
          <span className='text-neutral-50 text-xs'>Select up to 4 metrics</span>
        </div>
        <button onClick={onReset} className='text-neutral-50 text-xs hover:text-neutral-90 transition-colors'>
          Reset
        </button>
      </div>

      {/* List */}
      <div className='flex flex-col py-8 overflow-y-auto max-h-[400px]'>
        {metrics.map((metric) => {
          const isSelected = selected.includes(metric.id);
          const isDisabled = !isSelected && isMaxSelected;

          return (
            <button
              key={metric.id}
              type='button'
              className={cn(
                'flex items-center gap-8 px-16 py-8 cursor-pointer transition-colors focus:outline-none w-full text-left',
                isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-neutral-10'
              )}
              onClick={() => !isDisabled && onToggle(metric.id)}
              aria-label={`${isSelected ? 'Deselect' : 'Select'} ${metric.labelText || metric.label} metric`}
              disabled={isDisabled}
            >
              {/* Checkbox */}
              <div
                className={cn(
                  'w-16 h-16 rounded border flex items-center justify-center transition-all',
                  isSelected ? 'bg-[#00A651] border-[#00A651]' : 'bg-white border-neutral-40'
                )}
              >
                {isSelected && <CheckIcon className='text-white w-4 h-4 !text-[16px]' />}
              </div>

              {/* Icon */}
              <div className={cn('flex items-center justify-center', iconClassName)}>
                <img src={metric.icon} alt={metric.labelText || String(metric.label)} className='w-full h-full object-contain' />
              </div>

              {/* Label */}
              <span className='text-sm text-neutral-100 font-normal'>{metric.label}</span>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className='p-12 border-t border-neutral-20 bg-neutral-10 rounded-b-lg'>
        <Button
          variant='contained'
          fullWidth
          onClick={onApply}
          sx={{
            '&.Mui-disabled': {
              backgroundColor: '#BFBFBF',
              color: '#FFFFFF',
              cursor: 'not-allowed',
              opacity: 0.9,
            },
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};
